import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import {
  Activity,
  AlertTriangle,
  Check,
  FileLock2,
  FlaskConical,
  Info,
  Key,
  KeyRound,
  Moon,
  Palette,
  RotateCcw,
  Server,
  ShieldAlert,
  Sun,
  SunMoon,
  Tag,
  Timer,
  Trash2,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminSettingsGetAdminSettingsQueryKey,
  useAdminSettingsDeleteAdminSettingsAdyenWebhookHmac,
  useAdminSettingsGetAdminSettings,
  useAdminSettingsPutAdminSettings,
  useAdminSettingsPutAdminSettingsAdyenWebhookHmac,
} from '@/lib/api/admin-settings/admin-settings';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SaveButton } from '@/components/SaveButton';
import { useAuth, useHasRole } from '@/lib/auth/useAuth';
import { useMode } from '@/lib/mode/ModeProvider';

/**
 * Runtime-editable settings backed by the singleton system_settings row.
 * SuperAdmin can edit; lower roles see a read-only view.
 *
 * Caveat: edits to retry/timeout/breaker take effect on the next host
 * restart (the resilience pipeline binds these at boot). The
 * applicationName / applicationVersion fields are TRULY hot — the body
 * enricher reads them on every proxy call.
 */
const SettingsSchema = z
  .object({
    maxRetryAttempts: z.coerce.number().int().min(0).max(10),
    retryBaseDelayMs: z.coerce.number().int().min(50).max(5000),
    retryMaxDelayMs: z.coerce.number().int().min(200).max(60_000),
    attemptTimeoutSeconds: z.coerce.number().int().min(5).max(300),
    totalRequestTimeoutSeconds: z.coerce.number().int().min(5).max(600),
    circuitBreakerFailureRatio: z.coerce.number().min(0).max(1),
    circuitBreakerSamplingSeconds: z.coerce.number().int().min(10).max(600),
    circuitBreakerMinimumThroughput: z.coerce.number().int().min(1).max(1000),
    circuitBreakerBreakSeconds: z.coerce.number().int().min(5).max(3600),
    applicationName: z.string().min(1).max(80),
    applicationVersion: z.string().min(1).max(40),
    requestPayloadCaptureEnabled: z.boolean(),
    requestPayloadRetentionDays: z.coerce.number().int().min(1).max(365),
  })
  .refine((v) => v.retryMaxDelayMs >= v.retryBaseDelayMs, {
    path: ['retryMaxDelayMs'],
    message: 'Must be ≥ base delay.',
  })
  .refine((v) => v.totalRequestTimeoutSeconds >= v.attemptTimeoutSeconds, {
    path: ['totalRequestTimeoutSeconds'],
    message: 'Must be ≥ attempt timeout.',
  });

type SettingsForm = z.infer<typeof SettingsSchema>;

const Defaults: SettingsForm = {
  maxRetryAttempts: 5,
  retryBaseDelayMs: 200,
  retryMaxDelayMs: 5000,
  attemptTimeoutSeconds: 30,
  totalRequestTimeoutSeconds: 60,
  circuitBreakerFailureRatio: 0.5,
  circuitBreakerSamplingSeconds: 60,
  circuitBreakerMinimumThroughput: 5,
  circuitBreakerBreakSeconds: 30,
  applicationName: 'DWPaymentProxy',
  applicationVersion: '1.0.0',
  requestPayloadCaptureEnabled: true,
  requestPayloadRetentionDays: 30,
};

export default function SettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = useHasRole('SuperAdmin');
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const { mode } = useMode();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const settingsQuery = useAdminSettingsGetAdminSettings();
  const mutation = useAdminSettingsPutAdminSettings({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getAdminSettingsGetAdminSettingsQueryKey() });
        toast.success('Settings saved.');
      },
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 409) {
          toast.error('Settings were updated by someone else. Refresh and try again.');
        } else {
          toast.error('Could not save settings.');
        }
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<SettingsForm>({
    resolver: zodResolver(SettingsSchema),
    defaultValues: Defaults,
    // Orval types numeric fields as `number | string` because OpenAPI's
    // int32 schema is union-friendly. Coerce to plain number on the form
    // boundary so React Hook Form's value type stays narrow.
    values: settingsQuery.data
      ? {
          maxRetryAttempts: Number(settingsQuery.data.maxRetryAttempts),
          retryBaseDelayMs: Number(settingsQuery.data.retryBaseDelayMs),
          retryMaxDelayMs: Number(settingsQuery.data.retryMaxDelayMs),
          attemptTimeoutSeconds: Number(settingsQuery.data.attemptTimeoutSeconds),
          totalRequestTimeoutSeconds: Number(settingsQuery.data.totalRequestTimeoutSeconds),
          circuitBreakerFailureRatio: Number(settingsQuery.data.circuitBreakerFailureRatio),
          circuitBreakerSamplingSeconds: Number(settingsQuery.data.circuitBreakerSamplingSeconds),
          circuitBreakerMinimumThroughput: Number(settingsQuery.data.circuitBreakerMinimumThroughput),
          circuitBreakerBreakSeconds: Number(settingsQuery.data.circuitBreakerBreakSeconds),
          applicationName: settingsQuery.data.applicationName,
          applicationVersion: settingsQuery.data.applicationVersion,
          requestPayloadCaptureEnabled: settingsQuery.data.requestPayloadCaptureEnabled,
          requestPayloadRetentionDays: Number(settingsQuery.data.requestPayloadRetentionDays),
        }
      : undefined,
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate({
      data: {
        maxRetryAttempts: values.maxRetryAttempts,
        retryBaseDelayMs: values.retryBaseDelayMs,
        retryMaxDelayMs: values.retryMaxDelayMs,
        attemptTimeoutSeconds: values.attemptTimeoutSeconds,
        totalRequestTimeoutSeconds: values.totalRequestTimeoutSeconds,
        circuitBreakerFailureRatio: values.circuitBreakerFailureRatio,
        circuitBreakerSamplingSeconds: values.circuitBreakerSamplingSeconds,
        circuitBreakerMinimumThroughput: values.circuitBreakerMinimumThroughput,
        circuitBreakerBreakSeconds: values.circuitBreakerBreakSeconds,
        applicationName: values.applicationName,
        applicationVersion: values.applicationVersion,
        requestPayloadCaptureEnabled: values.requestPayloadCaptureEnabled,
        requestPayloadRetentionDays: values.requestPayloadRetentionDays,
        // The ingress-throttle ceiling (Stream A) isn't yet wired into this
        // form. Pass null so the PATCH endpoint leaves it unchanged. A
        // dedicated "Throughput" card belongs in a follow-up.
        maxConcurrentForwards: null,
        // Same story for audit-log retention — the new column is editable
        // server-side but doesn't yet have a UI control here. Send null so
        // the PATCH endpoint leaves the persisted value alone.
        auditLogRetentionDays: null,
      },
    });
  });

  return (
    <section className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Runtime-editable server config plus dashboard preferences. Each row of settings is scoped to the active
            environment — switch envs in the sidebar to edit the other one.
          </p>
        </div>
        {!isSuperAdmin && (
          <Badge variant="outline" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            Read-only · SuperAdmin can edit
          </Badge>
        )}
      </header>

      {/*
        Active-env banner — visual reminder that every setting on this page is
        being read from (and written to) the row matching the active env. The
        backend resolves the row by the JWT's `env` claim; the banner mirrors
        that on the UI so a SuperAdmin can't be surprised by which env's
        settings they're rotating an HMAC key for.
      */}
      <SettingsEnvBanner mode={mode} />

      {/*
        One Adyen webhook HMAC card, scoped to the active env. The same
        endpoint serves both envs; switch envs in the sidebar to manage the
        other env's key. Adyen Test and Adyen Live each have their own
        company-level HMAC configuration in Adyen Customer Area; this gateway
        picks the right key per inbound notification by reading the `live`
        field on each item.
      */}
      <AdyenWebhookHmacCard
        mode={mode}
        configured={settingsQuery.data?.adyenWebhookHmacKeyConfigured ?? false}
        loading={settingsQuery.isLoading}
        isSuperAdmin={isSuperAdmin}
      />

      {settingsQuery.isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <form
          onSubmit={onSubmit}
          className="space-y-4 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150"
          noValidate
        >
          <fieldset disabled={!isSuperAdmin} className="space-y-4 disabled:opacity-70">
            <SectionCard
              icon={<RotateCcw className="h-4 w-4" />}
              title="Retries"
              description="Polly retry strategy applied to upstream Adyen calls. Transient 5xx + transport failures only."
            >
              <NumberRow
                label="Max retry attempts"
                hint="0–10. Defaults to 5."
                error={errors.maxRetryAttempts?.message}
                input={<Input type="number" min={0} max={10} {...register('maxRetryAttempts')} />}
              />
              <NumberRow
                label="Retry base delay (ms)"
                hint="50–5000. Exponential backoff seed."
                error={errors.retryBaseDelayMs?.message}
                input={<Input type="number" min={50} max={5000} {...register('retryBaseDelayMs')} />}
              />
              <NumberRow
                label="Retry max delay (ms)"
                hint="200–60000. Cap on backoff. Must be ≥ base."
                error={errors.retryMaxDelayMs?.message}
                input={<Input type="number" min={200} max={60_000} {...register('retryMaxDelayMs')} />}
              />
            </SectionCard>

            <SectionCard
              icon={<Timer className="h-4 w-4" />}
              title="Timeouts"
              description="Per-attempt and total wall-clock budget for each /proxy/* call."
            >
              <NumberRow
                label="Attempt timeout (seconds)"
                hint="5–300. HTTP timeout per attempt."
                error={errors.attemptTimeoutSeconds?.message}
                input={<Input type="number" min={5} max={300} {...register('attemptTimeoutSeconds')} />}
              />
              <NumberRow
                label="Total request timeout (seconds)"
                hint="≥ attempt timeout. ≤ 600. Wall-clock cap for retries combined."
                error={errors.totalRequestTimeoutSeconds?.message}
                input={<Input type="number" min={5} max={600} {...register('totalRequestTimeoutSeconds')} />}
              />
            </SectionCard>

            <SectionCard
              icon={<Activity className="h-4 w-4" />}
              title="Circuit breaker"
              description="Opens upstream when failure ratio exceeds the threshold; trips for break-duration."
            >
              <NumberRow
                label="Failure ratio (0.0–1.0)"
                hint="Default 0.5 — opens when half the sample window failed."
                error={errors.circuitBreakerFailureRatio?.message}
                input={<Input type="number" step="0.05" min={0} max={1} {...register('circuitBreakerFailureRatio')} />}
              />
              <NumberRow
                label="Sampling duration (seconds)"
                hint="10–600. Window over which the ratio is computed."
                error={errors.circuitBreakerSamplingSeconds?.message}
                input={<Input type="number" min={10} max={600} {...register('circuitBreakerSamplingSeconds')} />}
              />
              <NumberRow
                label="Minimum throughput"
                hint="1–1000. Calls/window below this won't trip."
                error={errors.circuitBreakerMinimumThroughput?.message}
                input={<Input type="number" min={1} max={1000} {...register('circuitBreakerMinimumThroughput')} />}
              />
              <NumberRow
                label="Break duration (seconds)"
                hint="5–3600. How long the circuit stays open."
                error={errors.circuitBreakerBreakSeconds?.message}
                input={<Input type="number" min={5} max={3600} {...register('circuitBreakerBreakSeconds')} />}
              />
            </SectionCard>

            <SectionCard
              icon={<Tag className="h-4 w-4" />}
              title="Integration attribution"
              description="Stamped on every Adyen call's applicationInfo for support attribution. Hot — takes effect on the next call."
            >
              <NumberRow
                label="Application name"
                hint="Up to 80 chars."
                error={errors.applicationName?.message}
                input={<Input {...register('applicationName')} />}
              />
              <NumberRow
                label="Application version"
                hint="Up to 40 chars (semver recommended)."
                error={errors.applicationVersion?.message}
                input={<Input {...register('applicationVersion')} />}
              />
            </SectionCard>

            <SectionCard
              icon={<FileLock2 className="h-4 w-4" />}
              title="Audit & Capture"
              description="Persist masked, encrypted request and response bodies on every proxy call. Visible to Admins+ in the Transactions detail view."
            >
              <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_240px]">
                <div>
                  <Label htmlFor="requestPayloadCaptureEnabled" className="text-sm font-medium">
                    Enable payload capture
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Master switch for persisting per-call request, enriched, and response bodies. Turn off to stop new
                    rows being written; existing rows remain until reaped.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="requestPayloadCaptureEnabled"
                    {...register('requestPayloadCaptureEnabled')}
                    disabled={!isSuperAdmin}
                  />
                  <Label htmlFor="requestPayloadCaptureEnabled" className="text-sm text-muted-foreground">
                    Capture enabled
                  </Label>
                </div>
              </div>
              <NumberRow
                label="Retention (days)"
                hint="Older payload rows are dropped on the next reaper tick (~hourly). The transaction itself stays — only the body blobs are reaped."
                error={errors.requestPayloadRetentionDays?.message}
                input={<Input type="number" min={1} max={365} {...register('requestPayloadRetentionDays')} />}
              />
            </SectionCard>

            {settingsQuery.data && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <Info className="h-3 w-3" />
                Last updated by <span className="font-medium text-foreground">{settingsQuery.data.updatedBy}</span> on{' '}
                {new Date(settingsQuery.data.updatedAt).toLocaleString()}.
              </div>
            )}

            {isSuperAdmin && (
              <div className="flex items-center gap-2">
                <SaveButton
                  type="submit"
                  pending={mutation.isPending}
                  success={mutation.isSuccess}
                  disabled={!isDirty || isSubmitting}
                >
                  Save changes
                </SaveButton>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => reset(Defaults)}
                  disabled={isSubmitting || mutation.isPending}
                >
                  Restore defaults
                </Button>
                <p className="text-xs text-muted-foreground">
                  <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-500" />
                  Restart the API after editing retry / timeout / breaker.
                </p>
              </div>
            )}
          </fieldset>
        </form>
      )}

      {/* Browser-scoped preferences below — unchanged from the old placeholder. */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <CardTitle className="text-base">Appearance</CardTitle>
          </div>
          <CardDescription>Stored in the browser. Takes effect immediately.</CardDescription>
        </CardHeader>
        <CardContent>
          {mounted && (
            <div className="inline-flex rounded-md border border-border p-0.5 text-sm">
              {(
                [
                  { key: 'light', label: 'Light', icon: Sun },
                  { key: 'dark', label: 'Dark', icon: Moon },
                  { key: 'system', label: 'System', icon: SunMoon },
                ] as const
              ).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTheme(key)}
                  className={`flex items-center gap-2 rounded px-3 py-1.5 transition-colors ${
                    theme === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your account</CardTitle>
          <CardDescription>Signed in as</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">{user?.fullName}</span>
            <span className="text-xs text-muted-foreground">· {user?.email}</span>
            <Badge variant="outline">{user?.role}</Badge>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function SectionCard({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Adyen webhook HMAC key — per-environment secret. One value per env, stored
// on the matching system_settings row; the backend service routes writes to
// the row whose env column matches the active scope. Switch envs in the
// sidebar to manage the other env's key — the same endpoint serves both.
// ---------------------------------------------------------------------------

const HmacSchema = z.object({
  hmacKeyHex: z
    .string()
    .min(32, 'At least 32 hex characters.')
    .regex(/^[0-9a-fA-F]+$/, 'Hex only (0-9, a-f).')
    .refine((s) => s.length % 2 === 0, 'Hex must have even length.'),
});
type HmacForm = z.infer<typeof HmacSchema>;

/**
 * Active-env "what env am I editing?" banner pinned above every settings
 * card. Mirrors the sidebar's mode chip + the topbar sandbox banner so the
 * SuperAdmin always knows which env's row their next save will land on.
 */
function SettingsEnvBanner({ mode }: { mode: 'production' | 'sandbox' }) {
  const isProduction = mode === 'production';
  const Icon = isProduction ? Server : FlaskConical;
  const tone = isProduction
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200'
    : 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200';
  const envLabel = isProduction ? 'Production' : 'Sandbox';
  const detail = isProduction
    ? 'Live (Adyen Live) settings. Reads + writes target the Production row in system_settings.'
    : 'Sandbox (Adyen Test) settings. Reads + writes target the Sandbox row in system_settings.';

  return (
    <div className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${tone}`}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <div className="font-medium">Editing {envLabel} settings</div>
        <p className="text-xs opacity-90">{detail}</p>
      </div>
      <span className="rounded-full border border-current/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
        {envLabel}
      </span>
    </div>
  );
}

/**
 * One env-scoped HMAC card. Both routes (PUT + DELETE) target the same
 * backend service which scopes the row by the active env. Switching env in
 * the sidebar swaps which row is read/written — same UX, different row.
 */
function AdyenWebhookHmacCard({
  mode,
  configured,
  loading,
  isSuperAdmin,
}: {
  mode: 'production' | 'sandbox';
  configured: boolean;
  loading: boolean;
  isSuperAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getAdminSettingsGetAdminSettingsQueryKey() });

  const isProduction = mode === 'production';
  const envLabel = isProduction ? 'Production' : 'Sandbox';
  const envLabelLower = isProduction ? 'production' : 'sandbox';
  const envBadgeTone = isProduction
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200'
    : 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200';
  const platformLabel = isProduction ? 'Adyen Live' : 'Adyen Test';

  const setMutation = useAdminSettingsPutAdminSettingsAdyenWebhookHmac({
    mutation: {
      onSuccess: async () => {
        await invalidate();
        toast.success(`${envLabel} HMAC key set.`);
        setDialogOpen(false);
      },
      onError: () => toast.error(`Could not save ${envLabelLower} HMAC key.`),
    },
  });

  const clearMutation = useAdminSettingsDeleteAdminSettingsAdyenWebhookHmac({
    mutation: {
      onSuccess: async () => {
        await invalidate();
        toast.success(`${envLabel} HMAC key cleared.`);
      },
      onError: () => toast.error(`Could not clear ${envLabelLower} HMAC key.`),
    },
  });

  const setPending = setMutation.isPending;
  const clearPending = clearMutation.isPending;

  const submitKey = (hmacKeyHex: string) => {
    setMutation.mutate({ data: { hmacKeyHex } });
  };

  const onClear = async () => {
    const ok = await confirm({
      title: `Clear the ${envLabelLower} Adyen webhook HMAC key?`,
      description: `Until you set a new ${envLabelLower} key, every inbound Adyen ${
        isProduction ? 'Live' : 'Test'
      } notification will fail HMAC verification and the gateway will refuse to deliver it to partners.`,
      confirmLabel: 'Clear key',
      variant: 'destructive',
    });
    if (!ok) return;
    clearMutation.mutate();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            Adyen webhook signing key
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${envBadgeTone}`}
            >
              {envLabel}
            </span>
          </CardTitle>
          <CardDescription>
            HMAC key for inbound webhooks from <b>{platformLabel}</b>. Adyen Test and Adyen Live each have their own
            company-level HMAC configuration in Adyen Customer Area; this gateway picks the right key per notification
            by reading the <code className="font-mono text-[11px]">live</code> field. Partners never see this key.
          </CardDescription>
        </div>
        {!isSuperAdmin && (
          <Badge variant="outline" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            SuperAdmin only
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            {loading ? (
              <span className="text-muted-foreground">Loading…</span>
            ) : configured ? (
              <>
                <Check className="h-4 w-4 text-emerald-500" aria-hidden />
                <span className="font-medium">Configured</span>
                <span className="text-muted-foreground">
                  — inbound {platformLabel} webhooks are HMAC-verified.
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
                <span className="font-medium">Not configured</span>
                <span className="text-muted-foreground">
                  — {platformLabel} webhooks will be rejected.
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => setDialogOpen(true)}
              disabled={!isSuperAdmin || loading}
              title={!isSuperAdmin ? 'SuperAdmin only' : undefined}
            >
              <Key className="h-4 w-4" />
              {configured ? 'Rotate HMAC key' : 'Set HMAC key'}
            </Button>
            {configured && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onClear}
                disabled={!isSuperAdmin || loading || clearPending}
                title={!isSuperAdmin ? 'SuperAdmin only' : undefined}
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent>

      <SetHmacKeyDialog
        open={dialogOpen}
        rotating={configured}
        envLabel={envLabel}
        pending={setPending}
        onOpenChange={setDialogOpen}
        onSubmit={(values) => submitKey(values.hmacKeyHex)}
      />
    </Card>
  );
}

interface SetHmacKeyDialogProps {
  open: boolean;
  rotating: boolean;
  /** "Production" | "Sandbox" — disambiguates which env the dialog is editing. */
  envLabel: string;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: HmacForm) => void;
}

function SetHmacKeyDialog({ open, rotating, envLabel, pending, onOpenChange, onSubmit }: SetHmacKeyDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isValid },
  } = useForm<HmacForm>({
    resolver: zodResolver(HmacSchema),
    defaultValues: { hmacKeyHex: '' },
    mode: 'onChange',
  });

  // Strip whitespace as the operator pastes / blurs — Adyen renders the key
  // with spaces every 8 chars, and that triggers our zod regex error.
  const stripSpaces = () => {
    const current = getValues('hmacKeyHex') ?? '';
    const cleaned = current.replace(/\s+/g, '');
    if (cleaned !== current) {
      setValue('hmacKeyHex', cleaned, { shouldValidate: true, shouldDirty: true });
    }
  };

  const close = (next: boolean) => {
    if (!next) reset({ hmacKeyHex: '' });
    onOpenChange(next);
  };

  const { onBlur: rhfOnBlur, ...rhfRegister } = register('hmacKeyHex');

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {rotating ? `Rotate ${envLabel} Adyen webhook HMAC key` : `Set ${envLabel} Adyen webhook HMAC key`}
          </DialogTitle>
          <DialogDescription>
            Hex key from Adyen Customer Area → Developers → Webhooks → HMAC Key. Stored encrypted; never echoed back.
          </DialogDescription>
        </DialogHeader>

        {rotating && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            <span>
              Replaces the existing key. Any Adyen notification signed with the old key will be rejected as soon as
              you save. Update Adyen Customer Area&apos;s HMAC config in lockstep.
            </span>
          </div>
        )}

        <form
          onSubmit={handleSubmit((values) => onSubmit(values))}
          className="space-y-4"
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="platform-hmac-key">Adyen HMAC key (hex)</Label>
            <Input
              id="platform-hmac-key"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              placeholder="DFB1EB5485895CFA…"
              {...rhfRegister}
              onBlur={(e) => {
                stripSpaces();
                void rhfOnBlur(e);
              }}
              onPaste={() => {
                // Run after the paste lands in the input value.
                window.setTimeout(stripSpaces, 0);
              }}
            />
            {errors.hmacKeyHex && (
              <p className="text-xs text-destructive">{errors.hmacKeyHex.message}</p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Whitespace is stripped automatically — paste it as Adyen displays it.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting || pending}>
              {pending ? 'Saving…' : rotating ? 'Rotate key' : 'Save key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NumberRow({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_240px]">
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div>
        {input}
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}
